import React, { createContext, useContext, useState } from 'react';

const OcelContext = createContext();

export const OcelProvider = ({ children }) => {
  const [ocelData, setOcelData] = useState(null);
  const [isFiltered, setIsFiltered] = useState(false);
  const [metadata, setMetadata] = useState(null); // Store OCEL metadata including event types
  const [isMetadataLoading, setMetadataLoading] = useState(false);

  // Wrapper for setMetadata with logging
  const setMetadataWithLogging = React.useCallback((data) => {
    console.log('🔧 [OcelContext] setMetadata called with:', {
      hasData: !!data,
      eventTypesCount: data?.event_types?.length || 0,
      data: data
    });
    setMetadata(data);
  }, []);

  // Log metadata changes
  React.useEffect(() => {
    console.log('📡 [OcelContext] Metadata state changed:', {
      hasMetadata: !!metadata,
      eventTypesCount: metadata?.event_types?.length || 0
    });
  }, [metadata]);

  return (
    <OcelContext.Provider value={{
      ocelData,
      setOcelData,
      isFiltered,
      setIsFiltered,
      metadata,
      setMetadata: setMetadataWithLogging,
      isMetadataLoading,
      setMetadataLoading,
    }}>
      {children}
    </OcelContext.Provider>
  );
};

export const useOcelContext = () => useContext(OcelContext);

/**
 * Read OCEL metadata guarded by dataset: returns the shared metadata only if
 * it belongs to the given activeFile, otherwise null.
 *
 * After a dataset switch, the context still holds the OLD dataset's metadata
 * until the async refetch lands; remounted tiles reading it directly would
 * populate dropdowns with stale values. This read-time guard makes them see
 * "no metadata yet" instead — their [metadata, ...] effects re-run once the
 * correctly-tagged metadata arrives. Never clear the context value itself
 * (see CLAUDE.md: clearing races with the metadata sync effect).
 */
export const useOcelMetadata = (activeFile) => {
  const { metadata } = useContext(OcelContext) || {};
  return metadata && activeFile && metadata.file_type === activeFile
    ? metadata
    : null;
};
